import { useQuery } from '@tanstack/react-query';
import type { HandoverEntryLabel, HandoverOverview } from '@testflow/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IdLabel } from '../../components/atoms.js';
import { SidePanel } from '../../components/side-panel.js';
import { catalogApi } from '../../lib/catalog-api.js';
import { pickLang } from '../../lib/pick-lang.js';

// #195 (W5, Nachtrag 14.10.): „Tests von Kollegen" als Seitenleiste. Ein Kollege ist gewählt (Chip),
// darunter nur dessen übernehmbare EINTRÄGE (Testfall × Kette, nicht Testfälle — CONTEXT.md
// „Playlist-Eintrag"). Jede Zeile lässt sich aufklappen und zeigt den Testinhalt lesend, damit man
// vor dem Übernehmen sieht, was man bekommt. Was übernehmbar ist, entscheidet der Server
// (`overview.colleagues`); die Playlist-Suche filtert diese Leiste bewusst nicht — sie zeigt fremde
// Tests, die nie in der eigenen Liste standen.

// „blockiert" gelb wie die Playlist-Gruppe, „wartet" grau — nie dieselbe Farbe (CONTEXT.md:
// `waiting` und `blocked` sind verschiedene Zustände).
const LABEL_CLASS: Record<HandoverEntryLabel, string> = {
  open: 'text-text-body',
  retest: 'text-status-fail',
  repeat: 'text-status-fail',
  waiting: 'text-text-muted',
  blocked: 'text-status-warn',
};

type Colleagues = HandoverOverview['colleagues'];
type ColleagueEntry = Colleagues[number]['entries'][number];

const entryKey = (e: ColleagueEntry) => `${e.test_case_id}|${e.e2e_id ?? ''}`;

/** Zahl der übernehmbaren Einträge aller Kollegen — Zähler am Knopf „Tests von Kollegen (N)". */
export function countColleagueEntries(colleagues: Colleagues): number {
  return colleagues.reduce((sum, c) => sum + c.entries.length, 0);
}

export function ColleaguesPanel({
  projectId,
  colleagues,
  onTake,
  onClose,
  pending,
}: {
  projectId: string;
  colleagues: Colleagues;
  onTake: (testCaseId: string) => void;
  onClose: () => void;
  pending: boolean;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  // Vorauswahl: der erste Kollege mit Einträgen. Danach bleibt die Wahl stehen, auch wenn der
  // Kollege durch Übernehmen leer wird — dann sagt die Leiste das, statt still umzuspringen.
  const [selectedId, setSelectedId] = useState<string | null>(
    () => colleagues.find((c) => c.entries.length > 0)?.user_id ?? null,
  );
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const active =
    colleagues.find((c) => c.user_id === selectedId) ??
    colleagues.find((c) => c.entries.length > 0);
  const total = countColleagueEntries(colleagues);
  // Chips: wer noch Einträge hat, dazu der gewählte Kollege (auch bei 0) — sonst verschwände sein
  // Chip unter dem Mauszeiger. Bei Gesamtzahl 0 gar keine: dort steht nur der allgemeine Leertext,
  // kein „Name (0)".
  const chips =
    total === 0
      ? []
      : colleagues.filter((c) => c.entries.length > 0 || c.user_id === active?.user_id);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  return (
    <SidePanel title={t('handover.colleagues.title')} onClose={onClose}>
      {total === 0 && (
        <p data-colleagues-empty className="text-sm text-text-muted">
          {t('handover.colleagues.emptyAll')}
        </p>
      )}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {chips.map((c) => (
            <button
              key={c.user_id}
              type="button"
              aria-pressed={c.user_id === active?.user_id}
              onClick={() => setSelectedId(c.user_id)}
              className={`rounded-btn border px-2 py-1 text-xs ${
                c.user_id === active?.user_id
                  ? 'border-brand-accent bg-brand-accent/10 font-semibold text-text-primary'
                  : 'border-[var(--border)] bg-bg-surface text-text-body hover:text-text-primary'
              }`}
            >
              {t('handover.colleagues.chip', { name: c.name, count: c.entries.length })}
            </button>
          ))}
        </div>
      )}
      {active && active.entries.length === 0 && total > 0 && (
        <p data-colleagues-empty className="text-sm text-text-muted">
          {t('handover.colleagues.emptyColleague', { name: active.name })}
        </p>
      )}
      {active && active.entries.length > 0 && (
        <ul className="flex flex-col gap-2">
          {active.entries.map((e) => {
            const key = entryKey(e);
            const isOpen = expanded.has(key);
            const chain = pickLang(lang, e.e2e_name_de, e.e2e_name_en);
            return (
              <li
                key={key}
                className="flex flex-col gap-2 rounded-card border border-[var(--border)] px-3 py-2"
              >
                <div className="flex items-baseline gap-2 text-xs">
                  <IdLabel id={e.human_id} />
                  <span className="min-w-0 flex-1 truncate text-text-primary">
                    {pickLang(lang, e.title_de, e.title_en)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="min-w-0 flex-1 truncate text-text-muted">{chain}</span>
                  <span className={`shrink-0 font-semibold ${LABEL_CLASS[e.label]}`}>
                    {t(`handover.label.${e.label}`)}
                  </span>
                  <button
                    type="button"
                    aria-label={t('handover.takeAria', { id: e.human_id })}
                    disabled={pending}
                    onClick={() => onTake(e.test_case_id)}
                    className="shrink-0 rounded-btn border border-brand-accent px-2 py-0.5 text-[11px] font-semibold text-brand-accent disabled:opacity-50"
                  >
                    {t('handover.take')}
                  </button>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-label={t('handover.colleagues.detailsAria', { id: e.human_id })}
                    onClick={() => toggle(key)}
                    className="shrink-0 rounded-btn px-1 py-0.5 text-text-muted hover:text-text-primary"
                  >
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 12 12"
                      className={`h-3 w-3 ${isOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    >
                      <path d="M2 4.5 6 8.5 10 4.5" />
                    </svg>
                  </button>
                </div>
                {isOpen && <EntryDetails projectId={projectId} testCaseId={e.test_case_id} />}
              </li>
            );
          })}
        </ul>
      )}
    </SidePanel>
  );
}

// Testinhalt nur lesen, erst beim Aufklappen geladen. Gleicher Query-Key wie das Ausführungspanel
// (`['tc', …]`), damit ein schon geladener Testfall nicht noch einmal abgerufen wird.
function EntryDetails({ projectId, testCaseId }: { projectId: string; testCaseId: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const query = useQuery({
    queryKey: ['tc', projectId, testCaseId],
    queryFn: () => catalogApi.getTestCase(projectId, testCaseId),
  });

  if (query.isPending) {
    return <div className="text-xs text-text-muted">{t('common.loading')}</div>;
  }
  if (query.isError || !query.data) {
    return (
      <div role="alert" className="text-xs text-status-fail">
        {t('handover.colleagues.detailsError')}
      </div>
    );
  }

  const tc = query.data;
  const precondition = pickLang(lang, tc.precondition_de, tc.precondition_en);
  const expected = pickLang(lang, tc.expected_de, tc.expected_en);
  const steps = tc.steps.slice().sort((a, b) => a.step_no - b.step_no);

  return (
    <div
      data-colleagues-details
      className="flex flex-col gap-2 border-t border-[var(--border)] pt-2 text-xs"
    >
      {precondition && (
        <div>
          <div className="font-semibold text-text-muted">
            {t('handover.colleagues.precondition')}
          </div>
          <div className="whitespace-pre-line text-text-body">{precondition}</div>
        </div>
      )}
      {steps.length === 0 ? (
        <div className="text-text-muted">{t('handover.colleagues.noSteps')}</div>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[var(--border)] text-left text-[11px] font-semibold text-text-muted">
              <th className="w-8 px-1 py-1">{t('handover.colleagues.stepNo')}</th>
              <th className="px-1 py-1">{t('handover.colleagues.stepAction')}</th>
              <th className="px-1 py-1">{t('handover.colleagues.stepExpected')}</th>
            </tr>
          </thead>
          <tbody>
            {steps.map((s) => (
              <tr
                key={s.step_id}
                className="border-b border-[var(--border)] align-top last:border-0"
              >
                <td className="px-1 py-1 text-text-muted">{s.step_no}</td>
                <td className="whitespace-pre-line px-1 py-1 text-text-body">
                  {pickLang(lang, s.description_de, s.description_en) || '—'}
                </td>
                <td className="whitespace-pre-line px-1 py-1 text-text-body">
                  {pickLang(lang, s.expected_output_de, s.expected_output_en) || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {expected && (
        <div>
          <div className="font-semibold text-text-muted">{t('handover.colleagues.expected')}</div>
          <div className="whitespace-pre-line text-text-body">{expected}</div>
        </div>
      )}
    </div>
  );
}
